import { aiAlertAdMetrics } from '@utils/aiAlerts/metrics';

export const checkForErrorMessage = (dispatch, setCurrentConfigErrorMsg, currentConfig) => {
  const errorMsg = checkErrors(currentConfig);
  dispatch(setCurrentConfigErrorMsg(errorMsg));
  return;
};

export const validateSensitivityConfigs = (sensitivityData) => {
  return sensitivityData.some(
    (config) =>
      // check if error is there for even one config, save needs tp be disabled in that case
      !!checkErrors(config),
  );
};

export const checkErrors = (currentConfig) => {
  const errorVariable =
    currentConfig.metric === 'AUTOALERTPCP' || currentConfig.metric === 'APP_ACTIVE_DEVICES'
      ? 'Pcp Drop'
      : 'Impacted Unique';

  let error = '';

  const { app, metric } = currentConfig;
  // validation based on app/metrics
  if (app === 'ai' || aiAlertAdMetrics.includes(metric)) {
    if (hasErrorInAdConfig(currentConfig)) {
      error = 'Required values should be positive integers';
    }
  } else {
    if (hasErrorInWarningConfig(currentConfig)) {
      error = `Persistence and ${errorVariable} should all be set for Warning alert.`;
    } else if (hasErrorInCricticalConfig(currentConfig)) {
      error = `Persistence and ${errorVariable} should all be set for Critical alert.`;
    } else if (hasErrorInWarningValues(currentConfig)) {
      error = 'Warning values should be greater than Info values.';
    } else if (hasErrorInCriticalValues(currentConfig)) {
      error = 'Critical values should be greater than Info values.';
    } else if (hasErrorInWarningAndCritialCombination(currentConfig)) {
      error = 'Critical values should be greater than Warning values.';
    } else if (hasErrorInInfoConfig(currentConfig)) {
      error = 'Info values should be positive integers';
    }
  }
  return error;
};

// utils for validation
const isInteger = (values: any[] = []) => values.every((v) => Number.isInteger(v));
const isNull = (values: any[] = []) => values.every((v) => v === null);

// validate only asf and impactedUnqiue for ad metrics
const hasErrorInAdConfig = (currentConfig) => {
  const { asf, impactedUnique } = currentConfig;
  return !(isInteger([asf, impactedUnique]) || isNull([asf, impactedUnique]));
};

const hasErrorInInfoConfig = (currentConfig) => {
  const { sustain, impactedUnique, impactedUniquePct, sustainPcpDrop, metric } = currentConfig;

  if (metric === 'AUTOALERTPCP' || metric === 'APP_ACTIVE_DEVICES') {
    return !(
      (Number.isInteger(sustain) && Number.isInteger(sustainPcpDrop)) ||
      (sustain === null && sustainPcpDrop === null)
    );
  }

  return !(
    (Number.isInteger(sustain) &&
      Number.isInteger(impactedUnique) &&
      Number.isInteger(impactedUniquePct)) ||
    (sustain === null && impactedUnique === null && impactedUniquePct === null)
  );
};

const hasErrorInWarningConfig = (currentConfig) => {
  const {
    warningSustain,
    warningImpactedUnique,
    warningImpactedUniquePct,
    warningPcpDrop,
    metric,
  } = currentConfig;

  if (metric === 'AUTOALERTPCP' || metric === 'APP_ACTIVE_DEVICES') {
    return !(
      (Number.isInteger(warningSustain) && Number.isInteger(warningPcpDrop)) ||
      (warningSustain === null && warningPcpDrop === null)
    );
  }

  return !(
    (Number.isInteger(warningSustain) &&
      Number.isInteger(warningImpactedUnique) &&
      Number.isInteger(warningImpactedUniquePct)) ||
    (warningSustain === null && warningImpactedUnique === null && warningImpactedUniquePct === null)
  );
};

const hasErrorInCricticalConfig = (currentConfig) => {
  const {
    criticalSustain,
    criticalImpactedUnique,
    criticalImpactedUniquePct,
    criticalPcpDrop,
    metric,
  } = currentConfig;

  if (metric === 'AUTOALERTPCP' || metric === 'APP_ACTIVE_DEVICES') {
    return !(
      (Number.isInteger(criticalSustain) && Number.isInteger(criticalPcpDrop)) ||
      (criticalSustain === null && criticalPcpDrop === null)
    );
  }

  return !(
    (Number.isInteger(criticalSustain) &&
      Number.isInteger(criticalImpactedUnique) &&
      Number.isInteger(criticalImpactedUniquePct)) ||
    (criticalSustain === null &&
      criticalImpactedUnique === null &&
      criticalImpactedUniquePct === null)
  );
};

const hasErrorInWarningValues = (currentConfig) => {
  const {
    warningSustain,
    warningImpactedUnique,
    warningImpactedUniquePct,
    sustain,
    impactedUnique,
    impactedUniquePct,
    sustainPcpDrop,
    warningPcpDrop,
    metric,
  } = currentConfig;

  if (metric === 'AUTOALERTPCP' || metric === 'APP_ACTIVE_DEVICES') {
    return (
      (warningSustain && warningSustain <= sustain) ||
      (warningPcpDrop && warningPcpDrop <= sustainPcpDrop)
    );
  }
  return (
    (warningSustain && warningSustain <= sustain) ||
    (warningImpactedUnique && warningImpactedUnique <= impactedUnique) ||
    (warningImpactedUniquePct !== null && warningImpactedUniquePct < impactedUniquePct)
  );
};

const hasErrorInCriticalValues = (currentConfig) => {
  const {
    criticalSustain,
    criticalImpactedUnique,
    criticalImpactedUniquePct,
    sustain,
    impactedUnique,
    impactedUniquePct,
    criticalPcpDrop,
    sustainPcpDrop,
    metric,
  } = currentConfig;

  if (metric === 'AUTOALERTPCP' || metric === 'APP_ACTIVE_DEVICES') {
    return (
      (criticalSustain && criticalSustain <= sustain) ||
      (criticalPcpDrop && criticalPcpDrop <= sustainPcpDrop)
    );
  }

  return (
    (criticalSustain && criticalSustain <= sustain) ||
    (criticalImpactedUnique && criticalImpactedUnique <= impactedUnique) ||
    (criticalImpactedUniquePct !== null && criticalImpactedUniquePct < impactedUniquePct)
  );
};

const hasErrorInWarningAndCritialCombination = (currentConfig) => {
  const {
    criticalSustain,
    warningSustain,
    criticalImpactedUnique,
    warningImpactedUnique,
    criticalImpactedUniquePct,
    warningImpactedUniquePct,
    metric,
    warningPcpDrop,
    criticalPcpDrop,
  } = currentConfig;

  if (metric === 'AUTOALERTPCP' || metric === 'APP_ACTIVE_DEVICES') {
    return (
      (criticalSustain && warningSustain && criticalSustain <= warningSustain) ||
      (criticalPcpDrop && warningPcpDrop && criticalPcpDrop <= warningPcpDrop)
    );
  }

  return (
    (criticalSustain && warningSustain && criticalSustain <= warningSustain) ||
    (criticalImpactedUnique &&
      warningImpactedUnique &&
      criticalImpactedUnique <= warningImpactedUnique) ||
    (criticalImpactedUniquePct !== null &&
      warningImpactedUniquePct !== null &&
      criticalImpactedUniquePct < currentConfig.warningImpactedUniquePct)
  );
};
